import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { useContext } from 'react';
import watermarkImage from '../assets/coverzone-watermark.png';
import Header from '../Header';
import Sidebar from '../Sidebar';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = process.env.REACT_APP_API_URL;

interface Quotation {
  id: string;
  created_date: string;
  updated_date?: string;
  reference_number: string;
  client_name: string;
  client_id?: string;
  status: string;
  consultant_name: string;
  description: string;
  total_premium?: number;
  inception_date?: string;
  duration?: number;
  broker_fee?: number;
  management_fee?: number;
  created_by?: string;
}

interface Consultant {
  id: string;
  name: string;
  surname: string;
  email: string;
}

interface Client {
  id: string;
  name: string;
  surname: string;
  email: string;
  client_type?: string;
  contact_number?: string;
  mobile_number?: string;
  telephone_number?: string;
  organization?: string;
  personal?: boolean;
  commercial?: boolean;
  medical?: boolean;
  client_number?: string;
}

const ManageQuotations = () => {
  const { can } = usePermissions();
  const [user, setUser] = useState<{ id: number; email: string; username?: string; organization?: string } | null>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [filteredQuotations, setFilteredQuotations] = useState<Quotation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuotationTerm, setSearchQuotationTerm] = useState('');
  const [searchConsultantTerm, setSearchConsultantTerm] = useState('');
  const [currentUserFullName, setCurrentUserFullName] = useState<string>('');
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
        
        const userData = response.data;
        const fullName = userData.username || userData.email || '';
        if (fullName) {
          setCurrentUserFullName(fullName);
          setSearchConsultantTerm(fullName);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);

  // Fetch quotations
  useEffect(() => {
    const fetchQuotations = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        const response = await axios.get(`${API_URL}/api/manage/quotations`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        console.log('📦 Quotations response:', response.data);
        
        if (response.data.success) {
          const quotationsData = response.data.data || [];
          setQuotations(quotationsData);
          setFilteredQuotations(quotationsData);
        } else {
          throw new Error(response.data.message || 'Failed to fetch quotations');
        }
      } catch (error: any) {
        console.error('❌ Error fetching quotations:', error);
        setError(error.response?.data?.message || 'Failed to load quotations');
        
        // Fallback to mock data
        const mockQuotations: Quotation[] = [
  {
    id: '1',
    created_date: new Date().toISOString().split('T')[0],
    updated_date: new Date().toISOString().split('T')[0],
    reference_number: 'Q-001',
    client_name: 'Sample Client',
    client_id: '1',
    status: 'Open',
    consultant_name: 'Sample Consultant',
    description: 'Sample quotation description',
    total_premium: 1500.00,
    inception_date: new Date().toISOString().split('T')[0],
    duration: 12,
    broker_fee: 100.00,
    management_fee: 50.00,
    created_by: '1'
  }
];
        setQuotations(mockQuotations);
        setFilteredQuotations(mockQuotations);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuotations();
  }, []);

  // Search and filter
  useEffect(() => {
    let filtered = quotations;

    if (searchQuotationTerm.trim()) {
  const lowercasedSearch = searchQuotationTerm.toLowerCase().trim();
  filtered = filtered.filter(quotation =>
    quotation.reference_number?.toLowerCase().includes(lowercasedSearch) ||
    quotation.description?.toLowerCase().includes(lowercasedSearch) ||
    quotation.client_name?.toLowerCase().includes(lowercasedSearch) ||
    'open'.includes(lowercasedSearch)
  );
}

    if (searchConsultantTerm.trim()) {
      const lowercasedSearch = searchConsultantTerm.toLowerCase().trim();
      filtered = filtered.filter(quotation => {
        const quotationConsultantName = quotation.consultant_name?.toLowerCase().trim() || '';
        return quotationConsultantName.includes(lowercasedSearch);
      });
    }

    setFilteredQuotations(filtered);
  }, [searchQuotationTerm, searchConsultantTerm, quotations]);

    if (!can('quotes', 'read')) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">You do not have permission to access Quotations.</p>
      </div>
    );
  }

  const handleLogout = () => {
    authContext?.setUser(null);
    localStorage.removeItem('token');
    navigate('/login');
  };

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('Failed to load watermark image:', e);
  };

  const handleImageLoad = (type: string) => {
    //console.log(`${type} image loaded successfully`);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);
  };

  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex relative">

      {/* Watermark */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0">
        <img
          src={watermarkImage}
          alt="Watermark"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-100 h-100 opacity-5 z-0 select-none"
          onError={handleImageError}
          onLoad={() => handleImageLoad('Watermark')}
        />
      </div>

      {/* Sidebar */}
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />

      {/* Main Content */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        {/* Header */}
        <Header
          title="Quotations"
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
        />

        {/* Page Content */}
        <div className="flex-1 p-6 overflow-auto">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
              {error}
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-[1600px] mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Quotations</h2>
            </div>

            {/* Search Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Quotations
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by reference, type, description, client, or status..."
                    value={searchQuotationTerm}
                    onChange={(e) => setSearchQuotationTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Consultant
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by consultant name, surname, or email..."
                    value={searchConsultantTerm}
                    onChange={(e) => setSearchConsultantTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  {searchConsultantTerm && (
                    <button
                      onClick={() => setSearchConsultantTerm('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Search Results Info */}
            {(searchQuotationTerm || searchConsultantTerm) && (
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Showing {filteredQuotations.length} of {quotations.length} quotations
                  {searchQuotationTerm && ` matching "${searchQuotationTerm}"`}
                  {searchConsultantTerm && ` for consultant "${searchConsultantTerm}"`}
                </p>
              </div>
            )}

            {/* Quotations List */}
            {isLoading ? (
              <div className="text-center py-8">
                <p className="text-gray-700">Loading quotations...</p>
              </div>
            ) : filteredQuotations.length === 0 ? (
              <div className="text-center py-8">
                {searchQuotationTerm || searchConsultantTerm ? (
                  <p className="text-gray-700">No quotations found matching your search criteria.</p>
                ) : (
                  <p className="text-gray-700">No quotations available.</p>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
  <tr>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Date Created
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Last Updated
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Ref No
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Client Name
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Description
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Employee / Consultant
    </th>
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      Status
    </th>
  </tr>
</thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredQuotations.map((quotation) => (
                      <tr key={quotation.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(quotation.created_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(quotation.updated_date || quotation.created_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => {
                              if (quotation.client_id) {
                                navigate(`/personal/${quotation.client_id}`, { 
                                  state: { 
                                    activeTab: 'quotes',
                                    quoteId: quotation.id 
                                  } 
                                });
                              } else {
                                alert(`Cannot navigate to quotation details. No client associated with this quotation.`);
                              }
                            }}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          >
                            {quotation.reference_number && quotation.reference_number !== 'N/A' 
                              ? quotation.reference_number 
                              : `Q-${String(quotation.id).padStart(3, '0')}`}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {quotation.client_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="max-w-xs truncate" title={quotation.description}>
                            {quotation.description || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {quotation.consultant_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Open
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageQuotations;